import React, { memo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

import WallpaperOpt from '../WallpaperCard/WallpaperOpt';
import { CONTEXT } from '../../config';
import { WP_DOWNLOAD_WAIT, WP_DOWNLOAD_ING, WP_DOWNLOAD_FINISHED } from '../../constants/wallpaper';
import { logging, logPostInfo } from '../../actions/log';

import { clearWallpperList, setDetailList, showBatchAddInfo } from '../../actions/wallpaper';
import { hasClass, removeClass, addClass, changeClass, getQueryString } from '../../utils/utils';
import transparent from '../../static/transparent.svg';
import wpAuthorDefault from '../../static/wp_author_default.png';

import styles from './WallpaperCard.module.less';
import useLoginStatus from '../../utils/hooks/useLoginStatus';

import WpProductDownload from './WpProductDownloadBtn';
import WpTurnBtn from './WpTurnBtn';

const selectDownloadImgs = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpDowloadImgs,
);
const selectWpOpteration = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpOpteration,
);
const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

/**
 * cardInfo: {
 *   classType      // 用于判断此图片展示那个class
 *   isPreview      // 是否展示放大按钮
 *   isDeleteUsed   // 是否展示删除按钮，曾经使用的壁纸
 *   isDeleteColl   // 是否展示删除按钮，点过赞的
 *   isWallpaper    // 是否展示设为壁纸按钮
 *   isCollect      // 是否展示点赞按钮
 *   isPayCard      // 表示订单卡片 不展示作责、收藏相关信息
 *   isShowTurn     // 表示是否显示添加取消轮播按钮, 默认为true
 *
 *   pageSource     // 页面来源来判断，由此card进入详情页面后请求那个对应的接口数据
 *   code pageTage classId tagId colorId authorId ... // 为详情页请求接口所需要的的参数
 * }
 */
export default memo(({ wallpaper, cardInfo, logInfo, isFit = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  // 正在任务执行中的壁纸
  const wpDowloadImgs = useSelector(selectDownloadImgs);
  const wpOpteration = useSelector(selectWpOpteration);
  const logInfoData = useSelector(selectLogInfo);

  const [dowloadWP, setDowloadWP] = useState(null);
  const [imgClass, setImgClass] = useState(!isFit ? styles.small_image : styles.fit_image);
  const [collectNum, setCollectNum] = useState(wallpaper?.collectCount || 0);
  const loginStatus = useLoginStatus();
  const [wpProduct, setWpProduct] = useState({ hasBuy: false, show: false, hasSetPath: true });
  // 判断是否是在视口范围内
  const [refRenderDom, inRenderDom] = useInView({
    threshold: 0,
    skip: false,
    initialInView: true,
    rootMargin: '400px 0px 400px 0px',
    root: cardInfo?.node,
  });

  // 初始话className
  useEffect(() => {
    if (cardInfo.classType === 'small') {
      setImgClass(`${styles.small_image}  ${styles.space}`);
    } else if (cardInfo.classType === 'small_space') {
      setImgClass(`${styles.small_image}`);
    } else if (cardInfo.classType === 'big') {
      setImgClass(`${styles.big_image} ${styles.space}`);
    } else if (cardInfo.classType === 'big_space') {
      setImgClass(`${styles.big_image}`);
    } else if (cardInfo.classType === 'fit_space') {
      setImgClass(`${styles.fit_image}`);
    } else {
      setImgClass(`${styles.default_image}`);
    }
  }, [cardInfo]);

  useEffect(() => {
    // 初始化心
    if (wallpaper?.collectCount >= 10000) {
      setCollectNum(`${Math.round((wallpaper.collectCount / 10000) * 10) / 10}万`);
    } else {
      setCollectNum(wallpaper?.collectCount || 0);
    }
  }, [wallpaper && wallpaper.collectCount]);

  /**
   * 操作结束处理
   */
  useEffect(() => {
    if (
      wpOpteration &&
      wpOpteration?.wallpaperId === wallpaper?.wallpaperId &&
      wpOpteration.type === 1 &&
      wpOpteration.status === 0
    ) {
      const collectCount = wallpaper.hasCollect
        ? wallpaper.collectCount
        : wallpaper.collectCount + 1;
      if (collectCount >= 10000) {
        setCollectNum(`${Math.round((collectCount / 10000) * 10) / 10}万`);
      } else {
        setCollectNum(collectCount);
      }
    }
  }, [wpOpteration, wallpaper]);

  useEffect(() => {
    const found =
      wpDowloadImgs && wpDowloadImgs.find(wp => wp.wallpaperId === wallpaper.wallpaperId);
    setDowloadWP(found);
  }, [wpDowloadImgs]);

  const handleImg = useCallback(
    wp => {
      /**
       * 解决的问题：每一次进入详情页面这个图片顺序会变化（当第二次详情页面进入同一个list，但是list顺序会在请求完成之后变化）
       * 这时候redux中数据还是存在的，即showImg和wallpaperId有值。就会找到activeIndex切换一遍
       * 当请求回来的时候，showImg变化出发此函数再次运行，就会等到一个新的activeIndex再次切换一遍
       * 两次切换间隔太短，造成第二次切换无效。从而造成展示图片不是用户点击的图片
       */
      // dispatch(clearWallpperList());

      if (!wp?.wallpaperId) {
        return;
      }
      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'C_Wallpaper_Detail',
          label: '',
          param2: {
            source: `${logInfo.source}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            search_type:
              cardInfo?.pageSource === 'Search_Wallpaper' ||
              cardInfo?.pageSource === 'Search_Wallpaper_Reco'
                ? logInfoData.search_type
                : '',
            search_word:
              cardInfo?.pageSource === 'Search_Wallpaper' ||
              cardInfo?.pageSource === 'Search_Wallpaper_Reco'
                ? logInfoData.search_word
                : '',
          },
          param3: wallpaper?.bizInfo || '{}',
        }),
      );
      dispatch(
        logPostInfo({
          source_p: logInfo.source,
          source_pp: logInfoData.source_p,
        }),
      );
      dispatch(showBatchAddInfo(false));
      if (cardInfo.isSingleWp) {
        dispatch(setDetailList({ payload: { list: [wp] } }));
      }
      // 当来源不明，但是给了allData的时候，就用allData作为详情页面展示的数据
      if (cardInfo?.allData?.length > 0) {
        dispatch(setDetailList({ payload: { list: cardInfo?.allData } }));
      }
      setTimeout(() => {
        history.push(
          `${CONTEXT}/wpdetail?wallpaperId=${wp.wallpaperId}&pageSource=${cardInfo.pageSource ||
            'recommend'}&pageTage=${cardInfo.pageTage}&authorId=${cardInfo.authorId}&code=${
            cardInfo.code
          }&classId=${cardInfo.classId || ''}&tagId=${cardInfo.tagId ||
            ''}&colorId=${cardInfo.colorId || ''}&pay=${cardInfo.isHasPayPkg}`,
        );
      }, 20);
    },
    [logInfoData, cardInfo, wallpaper],
  );

  const onMouseOver = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName('wpimg_img')[0];
    addClass(el, styles.image_mouse_in);
    const el_phone = e.currentTarget.getElementsByClassName('wpimg_phone')[0];
    changeClass(el_phone, styles.card_cardOut, styles.card_cardIn);
  }, []);

  const onMouseLeave = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName('wpimg_img')[0];
    addClass(el, styles.image_mouse_out);
    const el_phone = e.currentTarget.getElementsByClassName('wpimg_phone')[0];
    changeClass(el_phone, styles.card_cardIn, styles.card_cardOut);
  }, []);

  const onAnimationEnd = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName('wpimg_img')[0];
    if (hasClass(el, styles.image_mouse_out)) {
      removeClass(el, styles.image_mouse_in);
      removeClass(el, styles.image_mouse_out);
    }
  }, []);

  const onError = function(e, flag) {
    if (e.currentTarget && e.currentTarget.src) {
      e.currentTarget.src = transparent;
    }
  };

  const handleAuthor = useCallback(
    author => {
      dispatch(
        logging({
          category: 'Wallpaper',
          action: 'C_Wallpaper_AuthorMark',
          label: '',
          param2: {
            source: `${logInfo.source}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            authorId: author.authorId,
            authorName: author.authorName,
            search_type:
              cardInfo?.pageSource === 'Search_Wallpaper' ||
              cardInfo?.pageSource === 'Search_Wallpaper_Reco'
                ? logInfoData.search_type
                : '',
            search_word:
              cardInfo?.pageSource === 'Search_Wallpaper' ||
              cardInfo?.pageSource === 'Search_Wallpaper_Reco'
                ? logInfoData.search_word
                : '',
          },
          param3: wallpaper?.bizInfo || '{}',
        }),
      );

      dispatch(
        logPostInfo({
          source_p: logInfo.source,
          source_pp: logInfoData.source_p,
        }),
      );

      setTimeout(() => {
        if (
          location.pathname !== `${CONTEXT}/wpauthordetail` &&
          getQueryString('authorId') !== author.authorId
        ) {
          history.push(`${CONTEXT}/wpauthordetail?authorId=${author.authorId}`);
        }
      }, 20);
    },
    [wallpaper, logInfoData],
  );

  // 登录状态发生变化，点赞收藏状态发生变化
  useEffect(() => {
    setWpProduct(state => ({
      ...state,
      hasBuy: false,
    }));
  }, [loginStatus]);

  return (
    <div className={`${styles.wpimg} ${imgClass} `} ref={refRenderDom}>
      {(cardInfo?.isNoNeedInView || inRenderDom) && (
        <div className={`${styles.wpimg_container}`}>
          <div
            className={`wpimg_box`}
            onMouseOver={e => onMouseOver(e, wallpaper)}
            onMouseLeave={e => onMouseLeave(e, wallpaper)}
            onAnimationEnd={e => onAnimationEnd(e)}
          >
            <img
              className="wpimg_img"
              src={
                (wallpaper?.wallpaperImg &&
                  wallpaper?.wallpaperImg[0] &&
                  wallpaper?.wallpaperImg[0]?.uri) ||
                transparent
              }
              onClick={() => handleImg(wallpaper)}
              onError={onError}
              alt="img"
            />
            {/* 当来自搜索的时候 */}
            {(cardInfo?.pageSource === 'Search_Wallpaper' ||
              cardInfo?.pageSource === 'Search_Wallpaper_Reco') &&
              wallpaper?.wallpaperImg &&
              ((wallpaper?.wallpaperImg[0] && wallpaper?.wallpaperImg[0]?.code === '4K') ||
                (wallpaper?.wallpaperImg[1] && wallpaper?.wallpaperImg[1]?.code === '4K') ||
                (wallpaper?.wallpaperImg[2] && wallpaper?.wallpaperImg[2]?.code === '4K') ||
                (wallpaper?.wallpaperImg[3] && wallpaper?.wallpaperImg[3]?.code === '4K')) && (
                <div className="wpimg_4k">4K</div>
              )}
            <div className="wpimg_info" onClick={() => handleImg(wallpaper)}>
              <div className="wpimg_info_box">
                {wallpaper?.pay === 1 && <div className="wpimg_info_v" />}
                <div className="wpimg_info_like">
                  {!cardInfo?.isPayCard && <>{collectNum || 0}收藏</>}
                </div>
              </div>

              <div className="wpimg_opt">
                <WallpaperOpt wallpaper={wallpaper} cardInfo={cardInfo} logInfo={logInfo} />
              </div>
            </div>
            {wallpaper?.supportDevice &&
              wallpaper?.supportDevice?.indexOf('PC') === -1 &&
              wallpaper?.supportDevice?.indexOf('PHONE') > -1 && (
                <div className="wpimg_phone"></div>
              )}

            <div className="wpimg_bottom">
              {cardInfo.isShowTurn !== false && (
                <WpTurnBtn
                  wallpaper={wallpaper}
                  cardInfo={cardInfo}
                  logInfo={logInfo}
                  classType={{ type: 'wp_img' }}
                />
              )}
              {wallpaper?.hasDownloadButton === 1 && (
                <WpProductDownload wallpaper={wallpaper} className="wpimg_downbtn" />
              )}
            </div>

            {/* 下载按钮 */}
          </div>
          {dowloadWP &&
            (dowloadWP.status === WP_DOWNLOAD_WAIT ||
              dowloadWP.status === WP_DOWNLOAD_ING ||
              dowloadWP.status === WP_DOWNLOAD_FINISHED) && (
              <div className="wpimg_busying">
                <div className="wpimg_busying_progress">
                  <div className="inner" style={{ width: `${dowloadWP.progress}%` }} />
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
});
